import { uniqBy } from 'lodash-es'
import type { AgentChatStatusDTO } from '@api/fastAPI.schemas'
import type { CopilotChatLabels, Interrupt } from '@copilotkit/react-core/v2'
import { AI_CHAT_AGENT_ID } from 'src/constants/aiChat'
import type { FindingSeverity, SceneFinding } from 'src/state/stores/aiChatFindings'
import BlockedMedia from './components/BlockedMedia'
import ChatLink from './components/ChatLink'
import ChatPre from './components/ChatPre'

/**
 * The local CopilotKit agent key for one thread. CopilotKit scopes agent
 * instances by this key; the runtime agent (`runtimeAgentId`) stays the one
 * registered in the Copilot runtime.
 */
export const localAgentId = (threadId: string): string => `${AI_CHAT_AGENT_ID}:${threadId}`

/** The user-facing strings of the prebuilt chat, in German. */
export const CHAT_LABELS: Partial<CopilotChatLabels> = {
  chatInputPlaceholder: 'Frage zum Projekt …',
  chatInputToolbarAddButtonLabel: 'Anhang hinzufügen',
  chatInputToolbarToolsButtonLabel: 'Werkzeuge',
  chatInputToolbarStartTranscribeButtonLabel: 'Diktieren',
  chatInputToolbarCancelTranscribeButtonLabel: 'Abbrechen',
  chatInputToolbarFinishTranscribeButtonLabel: 'Fertig',
  assistantMessageToolbarCopyCodeLabel: 'Kopieren',
  assistantMessageToolbarCopyCodeCopiedLabel: 'Kopiert',
  assistantMessageToolbarCopyMessageLabel: 'Kopieren',
  assistantMessageToolbarThumbsUpLabel: 'Gute Antwort',
  assistantMessageToolbarThumbsDownLabel: 'Schlechte Antwort',
  assistantMessageToolbarReadAloudLabel: 'Vorlesen',
  assistantMessageToolbarRegenerateLabel: 'Neu erzeugen',
  userMessageToolbarCopyMessageLabel: 'Kopieren',
  userMessageToolbarEditMessageLabel: 'Bearbeiten',
  chatDisclaimerText: 'Der Assistent kann sich irren. Wichtige Angaben bitte prüfen.',
  modalHeaderTitle: 'KI-Chat',
  welcomeMessageText:
    'Stellen Sie eine Frage zum geöffneten Projekt oder beschreiben Sie eine Änderung, z. B. ' +
    '„Welche Stockwerke hat das Projekt?“ oder „Setze alle Außenwände im EG auf 24 cm“. ' +
    'Jede Änderung legt der Assistent Ihnen vorher zur Freigabe vor.',
}

/** The run property `approvalMode` for the chat's auto-approve switch. */
export const approvalModeFor = (autoApprove: boolean): 'auto_approve' | 'every_change' =>
  autoApprove ? 'auto_approve' : 'every_change'

/**
 * The AG-UI `forwardedProps` of every chat run (the CopilotKit provider's
 * `properties`): the Copilot runtime hands them to the API's authorize route.
 * A type alias, not an interface, so it fits the provider's record prop.
 */
export type AIChatRunProperties = {
  projectGuid: string
  approvalMode: ReturnType<typeof approvalModeFor>
  model: string | undefined
}

/** What the agent attaches to a change it asks the user to approve (`approval_card`). */
export interface ProjectChangeCard {
  changeSummary: string | null
  method: string
  path: string
  body: string | null
}

const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null)

const isString = (value: unknown): value is string => typeof value === 'string'

/** The approval card of an interrupt, or null for an interrupt that is not a project change. */
export const projectChangeCardOf = (interrupt: Interrupt): ProjectChangeCard | null => {
  const metadata = interrupt.metadata as Record<string, unknown> | undefined
  if (!metadata || metadata.kind !== 'project_change') return null
  return {
    changeSummary: asString(metadata.change_summary),
    method: asString(metadata.method) ?? '',
    path: asString(metadata.path) ?? '',
    body: asString(metadata.body),
  }
}

/** What the agent attaches to its import plan (`propose_import_plan`). */
export interface ImportPlanCard {
  summary: string
  storeys: { name: string; lowerM: number; upperM: number }[]
  steps: string[]
  counts: [string, number][]
  notes: string | null
  /**
   * What `assign_ifc_elements` set in the IFC view (elements per type, filtered ones): the user
   * checks it in the scene before the import starts. Empty when the plan has none.
   */
  assignment: [string, number][]
}

/** The `[kind, count]` pairs of a counts object; entries that are not numbers are dropped. */
const countEntriesOf = (value: unknown): [string, number][] =>
  value && typeof value === 'object'
    ? Object.entries(value as Record<string, unknown>).flatMap(([kind, count]) =>
        typeof count === 'number' ? [[kind, count] as [string, number]] : [],
      )
    : []

/** The import plan of an interrupt, or null for an interrupt that is not one. */
export const importPlanCardOf = (interrupt: Interrupt): ImportPlanCard | null => {
  const metadata = interrupt.metadata as Record<string, unknown> | undefined
  if (!metadata || metadata.kind !== 'import_plan') return null
  const storeys = Array.isArray(metadata.storeys) ? metadata.storeys : []
  return {
    summary: asString(metadata.summary) ?? 'Importplan',
    storeys: storeys.map(storey => {
      const { name, lower_m: lowerM, upper_m: upperM } = storey as Record<string, unknown>
      return { name: asString(name) ?? '', lowerM: Number(lowerM), upperM: Number(upperM) }
    }),
    steps: Array.isArray(metadata.steps) ? metadata.steps.filter(isString) : [],
    counts: countEntriesOf(metadata.counts),
    notes: asString(metadata.notes),
    assignment: countEntriesOf(metadata.assignment),
  }
}

/** A decision the agent asks for with two buttons (`choice_card`). */
export interface ChoiceCard {
  question: string
  /** Runs the tool call. */
  approveLabel: string
  /** Declines the tool call with `denyReason` (what the agent reads). */
  denyLabel: string
  denyReason: string
  /** The standard option, shown as the primary button. */
  defaultOption: 'approve' | 'deny'
}

/** The choice of an interrupt, or null for an interrupt that is not one. */
export const choiceCardOf = (interrupt: Interrupt): ChoiceCard | null => {
  const metadata = interrupt.metadata as Record<string, unknown> | undefined
  if (!metadata || metadata.kind !== 'choice') return null
  const question = asString(metadata.question)
  const approveLabel = asString(metadata.approve_label)
  const denyLabel = asString(metadata.deny_label)
  if (!question || !approveLabel || !denyLabel) return null
  return {
    question,
    approveLabel,
    denyLabel,
    denyReason: asString(metadata.deny_reason) ?? denyLabel,
    defaultOption: metadata.default === 'approve' ? 'approve' : 'deny',
  }
}

/** One option of a question the agent asks (`ask_user`). */
export interface QuestionOption {
  id: string
  label: string
  description: string | null
}

/** A question with options the agent asks (`ask_user`): one choice, or several with `multiple`. */
export interface QuestionCard {
  question: string
  options: QuestionOption[]
  multiple: boolean
  /** The user may add free text ("Sonstiges"). */
  allowComment: boolean
  /** Option ids ticked at the start. */
  preselected: string[]
}

/** The question of an interrupt, or null for an interrupt that is not one. */
export const questionCardOf = (interrupt: Interrupt): QuestionCard | null => {
  const metadata = interrupt.metadata as Record<string, unknown> | undefined
  if (!metadata || metadata.kind !== 'question') return null
  const question = asString(metadata.question)
  const options = (Array.isArray(metadata.options) ? metadata.options : []).flatMap(entry => {
    const { id, label, description } = (entry ?? {}) as Record<string, unknown>
    return isString(id) && isString(label)
      ? [{ id, label, description: asString(description) }]
      : []
  })
  if (!question || options.length === 0) return null
  const ids = new Set(options.map(option => option.id))
  const multiple = metadata.multiple === true
  const preselected = (Array.isArray(metadata.preselected) ? metadata.preselected : []).filter(
    (id): id is string => isString(id) && ids.has(id),
  )
  return {
    question,
    options,
    multiple,
    allowComment: metadata.allow_comment === true,
    preselected: multiple ? preselected : preselected.slice(0, 1),
  }
}

/** The user's answer to a question: the chosen option ids and an optional comment. */
export interface QuestionAnswer {
  selected: string[]
  comment: string | null
}

/** A question in the chat history (`ask_user` with its result): what was asked and answered. */
export interface AnsweredQuestion {
  question: string
  /** The labels of the chosen options; empty when the question was not answered. */
  answer: string[]
  comment: string | null
}

/** The answered question of an `ask_user` tool call, or null for any other call or one still open. */
export const answeredQuestionOf = (
  name: string,
  args: unknown,
  result: string | undefined,
): AnsweredQuestion | null => {
  if (name !== 'ask_user' || result === undefined) return null
  const parameters = (args && typeof args === 'object' ? args : {}) as Record<string, unknown>
  const question = asString(parameters.question) ?? 'Frage'
  try {
    const parsed = JSON.parse(result) as Record<string, unknown>
    // The tool refused the call (e.g. too few options): no card was shown.
    if ('error' in parsed) return null
    const selected = Array.isArray(parsed.selected) ? parsed.selected : []
    return {
      question,
      answer: selected.flatMap(entry => {
        const label = (entry as Record<string, unknown> | null)?.label
        return isString(label) ? [label] : []
      }),
      comment: asString(parsed.comment),
    }
  } catch {
    // Not JSON: the question was not answered (the API's denial text).
    return { question, answer: [], comment: null }
  }
}

/** What the agent asks the user to upload in the chat (`request_files`). */
export interface FileRequestCard {
  fileKind: 'ifc' | 'floorplans'
  message: string | null
  storeys: { storey: string; hasPlan: boolean }[]
}

/** The file request of an interrupt, or null for an interrupt that is not one. */
export const fileRequestCardOf = (interrupt: Interrupt): FileRequestCard | null => {
  const metadata = interrupt.metadata as Record<string, unknown> | undefined
  if (!metadata || metadata.kind !== 'file_request') return null
  if (metadata.file_kind !== 'ifc' && metadata.file_kind !== 'floorplans') return null
  const storeys = Array.isArray(metadata.storeys) ? metadata.storeys : []
  return {
    fileKind: metadata.file_kind,
    message: asString(metadata.message),
    storeys: storeys.flatMap(entry => {
      const { storey, has_plan: hasPlan } = entry as Record<string, unknown>
      return isString(storey) ? [{ storey, hasPlan: hasPlan === true }] : []
    }),
  }
}

const metreFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

/** "2,897 m"; a dash for a value the agent did not give as a number. */
export const formatMetres = (value: number): string =>
  Number.isFinite(value) ? `${metreFormat.format(value)} m` : '–'

/** The AG-UI custom event the agent emits after every change that went through. */
export const PROJECT_CHANGED_EVENT = 'project_changed'

/** The AG-UI custom event with a storey the agent wants the scene to show. */
export const SHOW_STOREY_EVENT = 'show_storey'

/** The storey of a `show_storey` event value, or null for a malformed one. */
export const storeyOfShowStoreyEvent = (value: unknown): string | null => {
  const storey = (value as Record<string, unknown> | null | undefined)?.storey
  return typeof storey === 'string' && storey !== '' ? storey : null
}

/** The AG-UI custom event with the planar elements the agent marks in the scene (`mark_elements`). */
export const MARK_ELEMENTS_EVENT = 'mark_elements'

/** The element guids of a `mark_elements` event value (empty: unmark), or null for a malformed one. */
export const elementGuidsOfMarkElementsEvent = (value: unknown): string[] | null => {
  const guids = (value as Record<string, unknown> | null | undefined)?.element_guids
  if (!Array.isArray(guids)) return null
  return guids.filter((guid): guid is string => typeof guid === 'string' && guid !== '')
}

/** The AG-UI custom event with the findings of a check the agent shows in the scene. */
export const SHOW_FINDINGS_EVENT = 'show_findings'

const FINDING_SEVERITIES: readonly FindingSeverity[] = ['error', 'warning', 'info']

/**
 * The findings of a `show_findings` event value (empty: remove them), or null for a malformed
 * one. Findings without a known severity or without elements are dropped.
 */
export const findingsOfShowFindingsEvent = (value: unknown): SceneFinding[] | null => {
  const findings = (value as Record<string, unknown> | null | undefined)?.findings
  if (!Array.isArray(findings)) return null
  return findings.flatMap(entry => {
    const {
      rule,
      severity,
      title,
      detail,
      element_guids: guids,
    } = (entry ?? {}) as Record<string, unknown>
    const elementGuids = Array.isArray(guids)
      ? guids.filter((guid): guid is string => typeof guid === 'string' && guid !== '')
      : []
    if (!FINDING_SEVERITIES.includes(severity as FindingSeverity) || elementGuids.length === 0) {
      return []
    }
    return [
      {
        rule: asString(rule) ?? '',
        severity: severity as FindingSeverity,
        title: asString(title) ?? '',
        detail: asString(detail) ?? '',
        elementGuids,
      },
    ]
  })
}

/** The AG-UI custom event with an IFC model (its GLB file) the agent wants the scene to show. */
export const SHOW_IFC_MODEL_EVENT = 'show_ifc_model'

/** The GLB file guid of a `show_ifc_model` event value, or null for a malformed one. */
export const glbFileGuidOfShowIfcModelEvent = (value: unknown): string | null => {
  const guid = (value as Record<string, unknown> | null | undefined)?.glb_file_guid
  return typeof guid === 'string' && guid !== '' ? guid : null
}

/**
 * The model to run the next turn with: the preferred one if the catalogue
 * offers it (a thread's earlier model may have been removed from
 * `AGENT_MODELS` since), else the catalogue's default. Undefined until the
 * catalogue is loaded.
 */
export const resolveSelectedModel = (
  models: { id: string }[],
  preferred: string | null | undefined,
  defaultModel: string | undefined,
): string | undefined => {
  if (preferred && models.some(model => model.id === preferred)) return preferred
  if (defaultModel && models.some(model => model.id === defaultModel)) return defaultModel
  return models[0]?.id
}

const costFormat = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const tokenFormat = new Intl.NumberFormat('de-DE')

/**
 * The chat's running cost for the header chip: "0,42 $", "< 0,01 $" for
 * fractions of a cent, prefixed with "mind." when some turns have no known
 * cost (the sum is then a lower bound).
 */
export const formatChatCost = (costUsd: number, hasTurnsWithoutCost: boolean): string => {
  const amount =
    costUsd > 0 && costUsd < 0.01 ? `< ${costFormat.format(0.01)}` : costFormat.format(costUsd)
  return hasTurnsWithoutCost ? `mind. ${amount}` : amount
}

// Users who are neither developers nor admins see the chat's cost in credits.
const CREDITS_PER_USD = 1450
// From here on credits are shown in whole thousands ("12k Credits").
const THOUSANDS_FROM_CREDITS = 10_000
const creditFormat = new Intl.NumberFormat('de-DE', {
  useGrouping: false,
  maximumFractionDigits: 0,
})

/**
 * The chat's running cost in credits for users who are neither developers nor admins:
 * whole credits, rounded down ("2,35 $" is "3407 Credits"), whole thousands
 * from 10000 on ("10k Credits"), "< 1 Credit" for less than one, prefixed
 * with "mind." like `formatChatCost`.
 */
export const formatChatCredits = (costUsd: number, hasTurnsWithoutCost: boolean): string => {
  // Rounded to micro-credits first, so a float error never costs a whole credit.
  const credits = Math.floor(Math.round(costUsd * CREDITS_PER_USD * 1e6) / 1e6)
  const amount =
    costUsd > 0 && credits < 1
      ? '< 1 Credit'
      : credits >= THOUSANDS_FROM_CREDITS
      ? `${creditFormat.format(Math.floor(credits / 1000))}k Credits`
      : `${creditFormat.format(credits)} ${credits === 1 ? 'Credit' : 'Credits'}`
  return hasTurnsWithoutCost ? `mind. ${amount}` : amount
}

export const formatTokenCount = (tokens: number): string => tokenFormat.format(tokens)

/** "1 Anfrage", "3 Anfragen". */
export const formatRequestCount = (requests: number): string =>
  `${tokenFormat.format(requests)} ${requests === 1 ? 'Anfrage' : 'Anfragen'}`

const threadTimestampFormat = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short',
  timeStyle: 'short',
})

/** "07.10.26, 14:05" for the thread list; the raw string if it is not a date. */
export const formatThreadTimestamp = (iso: string): string => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : threadTimestampFormat.format(date)
}

/**
 * Element renderers for the chat's markdown: no media the browser would load
 * on its own in an answer (see BlockedMedia), links that are element
 * links where the agent names an element (see ChatLink), and the agent's
 * change summary as a card (see ChatPre).
 */
export const CHAT_MARKDOWN_COMPONENTS = {
  a: ChatLink,
  pre: ChatPre,
  img: BlockedMedia,
  picture: BlockedMedia,
  source: BlockedMedia,
  video: BlockedMedia,
  audio: BlockedMedia,
  iframe: BlockedMedia,
}

// Keys typed or pressed in the chat that the page behind it must not see.
const CHAT_KEY_EVENTS = ['keydown', 'keyup', 'keypress'] as const

/**
 * The DOM node the chat is rendered into for the lifetime of the session.
 * Moved, not re-rendered, into the drawer showing the chat (see
 * `ChatHostOutlet`), and kept while the chat is closed, so the
 * chat, its approval cards and their uploads stay mounted. Its key events
 * stop here: the page the chat covers keeps running (hidden) underneath, and
 * page hotkeys (document listeners, some active in text fields) must not act
 * on what the user types in the chat.
 */
export const createChatHost = (): HTMLDivElement => {
  const host = document.createElement('div')
  host.style.cssText = 'flex: 1; min-height: 0; display: flex; flex-direction: column;'
  for (const type of CHAT_KEY_EVENTS) {
    host.addEventListener(type, event => event.stopPropagation())
  }
  return host
}

/**
 * Marks the chat's uploads (`meta` of their mutations): while one runs, the
 * thread must not change, or its approval card -- which resumes the agent
 * once the upload is through -- would be unmounted on the way.
 */
export const AI_CHAT_UPLOAD_META = { aiChatUpload: true }

export const isAiChatUpload = (meta: Record<string, unknown> | undefined): boolean =>
  meta?.aiChatUpload === true

/** Why the chat cannot run here (`GET /agent-runs/status`), or null when it can. */
export const chatUnavailableReason = (status: AgentChatStatusDTO): string | null => {
  if (!status.enabled) return 'KI-Chat ist in dieser Umgebung nicht konfiguriert'
  // A limit of 0 means unlimited (the API's `assert_quota`).
  if (
    status.monthly_token_limit > 0 &&
    status.tokens_used_this_month >= status.monthly_token_limit
  ) {
    return 'KI-Chat: Das monatliche KI-Kontingent Ihrer Organisation ist aufgebraucht'
  }
  return null
}

/** What the agent hears when the user goes on without the requested files. */
export const NO_FILES_REASON = 'Der Nutzer hat keine Datei hochgeladen.'

// The tool results of a change that did not run: the API's denials
// (`approvals.py`), the frontend's own reason, and the agent's result for a
// change left undecided by a new message (`NOT_EXECUTED_RESULT`).
const DENIAL_RESULTS = new Set([
  'Vom Nutzer abgelehnt.',
  'Vom Nutzer abgebrochen.',
  'Nicht freigegeben: der Nutzer hat darüber nicht entschieden.',
  NO_FILES_REASON,
])
const NOT_EXECUTED_PREFIX = 'Nicht ausgeführt'

// The tools that ask before they change the project, with what their card
// says the change is (the agent's `workflow_card` texts); `call_operation`,
// `apply_changes` and `import_walls` carry the model's own `change_summary`.
const CHANGE_TOOL_TITLES: Record<string, string> = {
  call_operation: '',
  apply_changes: 'Änderungen am Projekt',
  import_walls: 'Wände aus der IFC importieren',
  propose_import_plan: 'Importplan',
  setup_storeys: 'Stockwerke anlegen und Grenzen setzen',
  import_roof: 'Dachflächen aus der IFC importieren',
  trim_roof_slabs: 'Dachflächen verschneiden',
  straighten_roof_ridge: 'Dachflächen am First zusammenführen',
  correct_outer_wall_axes: 'Achsen der Außenwände auf die Tragschicht verschieben',
  run_connection_pass: 'Verbindungs-Pass: freie Wandenden anschließen',
  align_floorplans: 'Hintergrundfolien einmessen',
  align_ifc_model: 'IFC-Modell ausrichten',
  fix_openings: 'Öffnungen abgleichen',
}

export type DecidedChangeOutcome = 'approved' | 'denied' | 'not_executed' | 'failed'

export interface DecidedChange {
  title: string
  outcome: DecidedChangeOutcome
  /** A file request (`request_files`): uploaded or left out, not approved or denied. */
  isFileRequest: boolean
}

const errorOfResult = (result: string): string | null => {
  try {
    const parsed: unknown = JSON.parse(result)
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const { error } = parsed as { error: unknown }
      return typeof error === 'string' ? error : ''
    }
  } catch {
    // Not JSON: a plain result.
  }
  return null
}

const outcomeOfResult = (result: string): DecidedChangeOutcome => {
  if (DENIAL_RESULTS.has(result)) return 'denied'
  const error = errorOfResult(result)
  if (error === null) return 'approved'
  return error.startsWith(NOT_EXECUTED_PREFIX) ? 'not_executed' : 'failed'
}

/**
 * A change the agent asked approval for, as the chat history keeps it: its
 * tool call and result. The approval card itself is not stored, so this is
 * what tells a reopened chat what was decided: from the result, a change ran
 * (approved, by the user or automatically), was denied, was left undecided
 * or failed. Null for every other tool call, reads included, and while the
 * call has no result yet (the approval card shows it then).
 */
export const decidedChangeOf = (
  name: string,
  args: unknown,
  result: string | undefined,
): DecidedChange | null => {
  if (result === undefined) return null
  const parameters = (args && typeof args === 'object' ? args : {}) as Record<string, unknown>
  if (name === 'request_files') {
    return {
      title: parameters.kind === 'ifc' ? 'IFC-Datei' : 'Hintergrundfolien',
      outcome: outcomeOfResult(result),
      isFileRequest: true,
    }
  }
  if (!(name in CHANGE_TOOL_TITLES)) return null
  const summary =
    typeof parameters.change_summary === 'string' ? parameters.change_summary.trim() : ''
  // A call_operation without a change summary is a read.
  if (name === 'call_operation' && !summary) return null
  return {
    title: summary || CHANGE_TOOL_TITLES[name],
    outcome: outcomeOfResult(result),
    isFileRequest: false,
  }
}

/**
 * Tells this client's runs from replayed ones, for agent subscriptions.
 * Connecting to a thread replays its whole history -- every earlier run with
 * all its events (custom events like `project_changed` included) -- through
 * the same callbacks as a live run. A replayed run starts with the
 * RUN_STARTED of its own, earlier run id; a live run's echoes the id this
 * client sent. Spread `callbacks` into the subscriber and check `isLive()`
 * in the handlers. (A run still going on when the chat reconnects counts as
 * replayed, which only happens after a reload.)
 */
export const createLiveRunTracker = () => {
  let live = true
  return {
    callbacks: {
      onRunInitialized: () => {
        live = true
      },
      onRunStartedEvent: ({
        event,
        input,
      }: {
        event: { runId: string }
        input: { runId: string }
      }) => {
        live = event.runId === input.runId
      },
    },
    isLive: () => live,
  }
}

const COUNT_LABELS: Record<FindingSeverity, [string, string]> = {
  error: ['Fehler', 'Fehler'],
  warning: ['Warnung', 'Warnungen'],
  info: ['Hinweis', 'Hinweise'],
}

/**
 * "2 Fehler, 3 Warnungen" for these findings, counted per element as the scene shows them (one
 * finding of four elements is four), a finding the agent passed twice once.
 */
export const countLabel = (findings: SceneFinding[]): string =>
  FINDING_SEVERITIES.map(severity => {
    const count = uniqBy(
      findings
        .filter(finding => finding.severity === severity)
        .flatMap(finding => finding.elementGuids.map(guid => ({ finding, guid }))),
      ({ finding, guid }) => JSON.stringify([finding.rule, finding.title, finding.detail, guid]),
    ).length
    const [one, many] = COUNT_LABELS[severity]
    return count === 0 ? null : `${count} ${count === 1 ? one : many}`
  })
    .filter(Boolean)
    .join(', ')
